import type React from 'react'
import type { LucideProps } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip'
import { cn } from '@/utils'

interface IconToggleButtonProps {
  icon: React.ComponentType<LucideProps>
  /** Both the accessible name and the tooltip text. */
  label: string
  pressed: boolean
  disabled: boolean
  onClick: () => void
  /** Overrides the panel-wide tooltip delay. */
  tooltipDelay?: number
  /**
   * Called when Escape closes this button's tooltip. The tooltip takes that Escape (and marks it
   * handled), so without this the canvas never sees it and a focused icon needs a second press to
   * deselect. Not called when no tooltip is open: then Escape reaches the canvas as usual.
   */
  onEscape?: () => void
}

/** An outline icon button that reads as pressed (accent) when its option is active. */
export function IconToggleButton({
  icon: Icon,
  label,
  pressed,
  disabled,
  onClick,
  tooltipDelay,
  onEscape,
}: IconToggleButtonProps) {
  return (
    <Tooltip delayDuration={tooltipDelay}>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Button
            variant="outline"
            size="icon"
            disabled={disabled}
            onClick={onClick}
            aria-label={label}
            aria-pressed={pressed}
            className={cn('text-muted-foreground', pressed && 'bg-accent text-accent-foreground')}
          >
            <Icon className="h-4 w-4" />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" onEscapeKeyDown={() => onEscape?.()}>
        {label}
      </TooltipContent>
    </Tooltip>
  )
}
